import { useEffect, useState } from "react";
import { Share, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * "홈 화면에 추가" 안내.
 *
 * 브라우저마다 방식이 **완전히 다르다**:
 *  - 안드로이드/크롬 계열: `beforeinstallprompt` 이벤트를 붙잡아 두면 **버튼 한 번**으로 설치된다.
 *  - iOS 사파리: 그 이벤트가 없다. 공유 시트를 거치는 수밖에 없어서 **순서를 글로** 알려준다.
 *
 * 그래서 하나의 UI로 합치지 않고 둘로 나눴다. 합치면 안드로이드에서 필요 없는
 * 설명이 붙고, iOS에서는 눌러도 아무 일이 없는 버튼이 생긴다.
 *
 * 안 보이는 경우: 이미 설치됨(standalone), 닫은 적 있음, 설치 대상이 아닌 브라우저.
 */
const DISMISS_KEY = "gotravel:installDismissed";

type InstallEvent = Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: string }> };

/** 이미 앱으로 실행 중인가. iOS는 표준 API가 없어 벤더 속성을 본다. */
function isStandalone(): boolean {
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  return (navigator as unknown as { standalone?: boolean }).standalone === true;
}

/** iOS 사파리인가. 최신 iPad는 UA가 Macintosh라 터치 지원까지 같이 본다. */
function isIos(): boolean {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

function dismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false; // 저장소가 막혀 있으면 매번 보여준다 — 숨기는 것보다 낫다.
  }
}

export function InstallPrompt() {
  const [event, setEvent] = useState<InstallEvent | null>(null);
  const [showIos, setShowIos] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissed()) return;

    // 안드로이드: 이벤트를 붙잡아 둔다. **기본 동작을 막아야** 브라우저가 자기
    // 배너를 띄우지 않고 우리 버튼이 그 자리를 대신한다.
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setEvent(e as InstallEvent);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS: 이벤트가 영영 안 온다. 사파리이고 아직 설치 전이면 안내를 띄운다.
    if (isIos()) setShowIos(true);

    // 설치가 끝나면 즉시 치운다.
    const onInstalled = () => setClosed(true);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  function close() {
    setClosed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // 못 저장해도 이번 세션에서는 닫힌다.
    }
  }

  async function install() {
    if (!event) return;
    await event.prompt();
    await event.userChoice.catch(() => null);
    // 수락이든 거절이든 이벤트는 한 번만 쓸 수 있다 — 다시 못 띄우므로 치운다.
    setEvent(null);
  }

  if (closed || (!event && !showIos)) return null;

  return (
    <div className="border-b border-border/70 bg-muted/60 px-4 py-2 text-sm print:hidden sm:hidden">
      <div className="mx-auto flex w-full max-w-5xl items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {event ? (
            <span className="font-medium">홈 화면에 추가하면 앱처럼 쓸 수 있어요</span>
          ) : (
            <>
              <span className="font-medium">홈 화면에 추가하기</span>
              {/* iOS는 눌러서 되는 게 아니라 순서를 따라가야 한다. */}
              <ol className="flex flex-col gap-0.5 text-xs text-muted-foreground">
                <li className="flex items-center gap-1.5">
                  1. 아래 <Share className="size-3.5" aria-label="공유" /> 공유 버튼
                </li>
                <li>2. 목록을 내려 <b>더 보기</b></li>
                <li className="flex items-center gap-1.5">
                  3. <Plus className="size-3.5" aria-hidden /> <b>홈 화면에 추가</b>
                </li>
              </ol>
            </>
          )}
        </div>

        {event && (
          <Button size="sm" className="shrink-0" onClick={install}>
            추가
          </Button>
        )}
        <button
          type="button"
          onClick={close}
          aria-label="안내 닫기"
          className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
